import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { SearchService } from '../../../pages/search/services/search.service';

interface LocationIntelligenceData {
  label: string;
  value: string | number | null;
}

@Component({
  selector: 'app-record-location-intelligence',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './record-location-intelligence.component.html',
  styleUrl: './record-location-intelligence.component.scss',
})
export class RecordLocationIntelligenceComponent
  extends DestroyBase
  implements OnInit
{
  @Input() uniqueId!: string;
  @Output() viewMoreClicked = new EventEmitter<Record<string, unknown>>();

  searchService = inject(SearchService);

  isLoading = false;
  errorMessage = '';
  programDetails: LocationIntelligenceData[] = [];
  fullResponseData: Record<string, unknown> | undefined = undefined;

  ngOnInit(): void {
    this.loadLocationIntelligence();
  }

  loadLocationIntelligence(): void {
    if (!this.uniqueId) {
      this.errorMessage = 'No record ID provided';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.searchService
      .getRecordArcgisIntersectionData(this.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response) {
            this.fullResponseData = response; // Store full response for later use
            this.processProgramDetails(response);
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Location Intelligence Error:', error);
          this.errorMessage = 'Failed to load location intelligence data';
        },
      });
  }

  private processProgramDetails(data: Record<string, unknown>): void {
    const details: LocationIntelligenceData[] = [];

    // Helper function to get value or N/A
    const getValue = (val: unknown): string | number => {
      if (val === undefined || val === null || val === '') {
        return 'N/A';
      }

      if (typeof val === 'string' || typeof val === 'number') {
        return val;
      }

      return 'N/A';
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getLayerValueByKey = (key: string): Record<string, any> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const layer = data?.[key] as { value?: Record<string, any> } | undefined;
      return layer?.value ?? {};
    };

    // CDFI IA 2023
    const cdfi = getLayerValueByKey('cdfi_investment_areas');

    details.push({
      label: 'CDFI Investment Area 2023 (Ia2020)',
      value: getValue(cdfi['ia2020']),
    });

    // Low to Moderate Income Population by Tract
    const lmi = getLayerValueByKey('low_to_moderate_income_areas');

    let lmiValue: string | number = 'N/A';
    const lowModPct = lmi['LOWMODPCT'];

    if (lowModPct !== undefined && lowModPct !== null) {
      const numericValue =
        typeof lowModPct === 'number'
          ? lowModPct
          : parseFloat(String(lowModPct));

      if (!isNaN(numericValue)) {
        lmiValue = numericValue > 51 ? 'Yes' : 'No';
      }
    }
    details.push({
      label: 'LMI Areas',
      value: lmiValue,
    });

    // Regrid Nationwide Parcel Data
    const regrid = getLayerValueByKey('parcels');

    details.push({
      label: 'Federal Qualified Opportunity Zone',
      value: getValue(regrid['qoz']),
    });

    details.push({
      label: 'Qualified Opportunity Zone Tract Number',
      value: getValue(regrid['qoz_tract']),
    });

    details.push({
      label: 'Census 2020 Tract',
      value: getValue(regrid['census_tract']),
    });

    details.push({
      label: 'Census 2020 Block',
      value: getValue(regrid['census_block']),
    });

    details.push({
      label: 'Census Zip Code Tabulation Area',
      value: getValue(regrid['census_zcta']),
    });

    this.programDetails = details;
  }

  onViewMore(): void {
    if (this.fullResponseData) {
      this.viewMoreClicked.emit(this.fullResponseData);
    }
  }
}
